import { useEffect, useState } from 'react'

import { ActionIcon, Divider, Select, Tooltip } from '@mantine/core'
import { DatesProvider } from '@mantine/dates'
import { useDisclosure } from '@mantine/hooks'
import dayjs from 'dayjs'

import { useQuery, useMutation } from '@redwoodjs/web'

import { toast } from 'src/components/Overlays/Toast/Toast'
import {
  GET_APPLICATION_BY_APPLICANT_ID,
  UPDATE_APPLICATION,
} from 'src/graphql/application.graphql'
import { ApplicationStatus } from 'src/graphql/types/applications'
import IconShareOpen from 'src/icons/IconShareOpen'

import CompanyQuestionsCard from '../ApplicationTab/components/CompanyQuestionsCard'
import JobInformationCard from '../ApplicationTab/components/JobInformationCard/JobInformationCard'
import ApplicantSharingModal from '../ApplicationTab/components/Modals/ApplicantSharingModal'
import PersonalInformationCard from '../ApplicationTab/components/PersonalInformationCard'
import ResumeCard from '../ApplicationTab/components/ResumeCard/ResumeCard'
import {
  getJobInformation,
  getPersonalInformation,
} from '../ApplicationTab/generateData'

interface OfficeApplicationTabProps {
  applicantId: string
  onRender: any
  onApplicantNotFound: () => void
}

const OfficeApplicationTab = ({
  applicantId,
  onRender,
  onApplicantNotFound,
}: OfficeApplicationTabProps) => {
  const [opened, { open, close }] = useDisclosure(false)
  const [updatedItems] = useState({})

  const { data: applicationResponse } = useQuery(
    GET_APPLICATION_BY_APPLICANT_ID,
    {
      variables: {
        applicantId,
      },
      onError: () => {
        toast('Unable to get application, please try again.', 'error')
      },
    }
  )

  const application = applicationResponse?.applicationByApplicantId

  const [updateApplication] = useMutation(UPDATE_APPLICATION, {
    variables: {
      id: applicationResponse?.applicationByApplicantId?.applicationId,
      input: { ...updatedItems },
    },
    refetchQueries: [
      {
        query: GET_APPLICATION_BY_APPLICANT_ID,
        variables: {
          applicantId,
        },
      },
      ...onRender,
    ],
    onCompleted: () => {
      toast('Application updated successfully', 'success')
    },
    onError: () => {
      toast('Unable to update application, please try again.', 'error')
    },
  })

  const updateStatus = (value) => {
    updateApplication({
      variables: {
        id: applicationResponse?.applicationByApplicantId?.applicationId,
        input: {
          status: value,
        },
      },
    })
  }

  const handleUpdateApplication = async (value) => {
    return await updateApplication({
      variables: {
        id: applicationResponse?.applicationByApplicantId?.applicationId,
        input: {
          ...value,
        },
      },
    })
  }

  useEffect(() => {
    if (applicationResponse && !application) {
      onApplicantNotFound()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationResponse, application])

  if (!applicationResponse) return null

  if (!application) {
    return null
  }

  return (
    <DatesProvider settings={{ timezone: 'UTC' }}>
      <div className="flex h-full flex-col gap-5">
        {opened && (
          <ApplicantSharingModal
            isModalOpen={opened}
            onModalClose={close}
            applicationId={application.applicationId}
          />
        )}
        <div className="flex h-[88%] flex-col gap-4 overflow-auto">
          <div>
            <div className="flex flex-row items-center gap-4 py-2">
              <div className="text-lg font-semibold text-doubleNickel-black">
                Application
              </div>
              <Select
                value={application?.status}
                onChange={(value) => updateStatus(value)}
                data={[
                  {
                    label: 'Not Started',
                    value: ApplicationStatus.NOT_STARTED,
                  },
                  {
                    label: 'In Progress',
                    value: ApplicationStatus.IN_PROGRESS,
                  },
                  {
                    label: 'Pending Review',
                    value: ApplicationStatus.PENDING_REVIEW,
                  },
                  { label: 'Completed', value: ApplicationStatus.COMPLETED },
                ]}
                classNames={{
                  root: 'w-40',
                  option: 'text-xs',
                  input: `rounded-lg text-xs font-semibold ${
                    application?.status === ApplicationStatus.IN_PROGRESS &&
                    'bg-doubleNickel-brand-25 border border-doubleNickel-brand-500 text-doubleNickel-brand-500 '
                  }
              ${
                application?.status === ApplicationStatus.PENDING_REVIEW &&
                'bg-doubleNickel-warning-50 border border-doubleNickel-warning-500 text-doubleNickel-warning-600 '
              }
              ${
                application?.status === ApplicationStatus.COMPLETED &&
                'bg-doubleNickel-success-50 border border-doubleNickel-success-600 text-doubleNickel-success-600 '
              }`,
                }}
                allowDeselect={false}
              />
              <div className="text-xs font-medium text-doubleNickel-gray-600">
                Last updated:{' '}
                {dayjs(application?.updatedAt).format('HH:mm:ss on MM/DD/YYYY')}
              </div>
              <Tooltip label="Share">
                <ActionIcon variant="subtle" className="ml-auto" onClick={open}>
                  <IconShareOpen className="max-h-5 fill-none stroke-doubleNickel-gray-700" />
                </ActionIcon>
              </Tooltip>
            </div>
            <Divider />
          </div>
          <div className="flex flex-col gap-4 overflow-auto">
            <JobInformationCard
              {...getJobInformation(application)}
              handleUpdateApplication={handleUpdateApplication}
              initialOpened={false}
            />
            <PersonalInformationCard
              {...getPersonalInformation(application)}
              handleUpdateApplication={handleUpdateApplication}
              initialOpened={false}
            />
            <CompanyQuestionsCard
              applicationId={application?.applicationId}
              applicantId={applicantId}
              jobListingId={application?.applicant?.jobListing?.jobListingId}
              questionResponses={application?.questionResponses}
              onUpdate={() => {
                // Refetch application data after update
              }}
            />
            <ResumeCard applicantId={applicantId} />
          </div>
        </div>
      </div>
    </DatesProvider>
  )
}

export default OfficeApplicationTab
